import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { APP_NAME } from "../config/brand";
import {
  isAndroidDevice,
  isIosDevice,
  isSecureContextForPwa,
  isStandaloneDisplayMode,
} from "../pwa/pwaDetection";
import {
  PWA_STORAGE_KEYS,
  clearInstallDismissals,
  dismissForAWhile,
  isDismissedRecently,
  readPreferAppLayout,
  writePreferAppLayout,
} from "../pwa/pwaStorage";

/** @typedef {'android' | 'ios' | 'desktop' | null} InstallPlatform */

/**
 * @typedef {Object} InstallInstructions
 * @property {InstallPlatform} platform
 * @property {boolean} showPrompt — whether UI should surface install help
 * @property {string} title
 * @property {string[]} steps
 * @property {string | null} actionLabel — e.g. "Install app" on Android when canInstall
 * @property {'banner' | 'card' | 'settings' | null} variant
 */

export const PwaContext = createContext(null);

/**
 * Builds platform-specific install copy for banners, cards, and settings pages.
 * @param {Object} state
 * @returns {InstallInstructions}
 */
export function getInstallInstructions({
  isStandalone,
  isAndroid,
  isIOS,
  isSecure,
  canInstall,
  showAndroidInstallBanner,
  showIosInstallInstructions,
}) {
  if (isStandalone) {
    return {
      platform: null,
      showPrompt: false,
      title: "",
      steps: [],
      actionLabel: null,
      variant: null,
    };
  }

  if (isAndroid) {
    const steps = canInstall
      ? [
          `Tap "${APP_NAME}" install when prompted.`,
          "Or open the browser menu → Install app / Add to Home screen.",
          "Open from your home screen for fullscreen — same login and data.",
        ]
      : [
          "Use Chrome over HTTPS (production URL).",
          "When install is available, tap Install app in the banner or App settings.",
          "Same account and school data as the website.",
        ];

    return {
      platform: "android",
      showPrompt: showAndroidInstallBanner,
      title: `Install ${APP_NAME}`,
      steps,
      actionLabel: canInstall ? "Install app" : null,
      variant: showAndroidInstallBanner ? "banner" : "settings",
    };
  }

  if (isIOS) {
    return {
      platform: "ios",
      showPrompt: showIosInstallInstructions,
      title: `Add ${APP_NAME} to Home Screen`,
      steps: [
        "Tap the Share button in Safari (square with an arrow at the bottom).",
        'Scroll and tap "Add to Home Screen", then confirm Add.',
        "Open the icon on your home screen for a fullscreen app with the same login.",
      ],
      actionLabel: null,
      variant: showIosInstallInstructions ? "card" : "settings",
    };
  }

  if (!isSecure) {
    return {
      platform: "desktop",
      showPrompt: false,
      title: `Install ${APP_NAME}`,
      steps: [
        "Use HTTPS in production to enable install.",
        "On desktop Chrome/Edge: menu → Install app or Create shortcut.",
        "On mobile: Chrome (Android) or Safari (iOS) as above.",
      ],
      actionLabel: null,
      variant: "settings",
    };
  }

  return {
    platform: "desktop",
    showPrompt: false,
    title: `Install ${APP_NAME}`,
    steps: [
      "Desktop: browser menu → Install app / Create shortcut.",
      "Android: Chrome install prompt or Add to Home screen.",
      "iOS: Safari Share → Add to Home Screen.",
    ],
    actionLabel: null,
    variant: "settings",
  };
}

/**
 * Internal state hook — used by PwaProvider. Prefer `usePWA()` in components.
 */
export function usePwaState() {
  const [standalone, setStandalone] = useState(() => isStandaloneDisplayMode());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [androidDismissed, setAndroidDismissed] = useState(() =>
    isDismissedRecently(PWA_STORAGE_KEYS.androidBannerDismissedAt)
  );
  const [iosDismissed, setIosDismissed] = useState(() =>
    isDismissedRecently(PWA_STORAGE_KEYS.iosInstructionsDismissedAt)
  );
  const [preferAppLayout, setPreferAppLayoutState] = useState(() => {
    const stored = readPreferAppLayout();
    if (stored !== null) return stored;
    return isStandaloneDisplayMode();
  });

  const isIOS = useMemo(() => isIosDevice(), []);
  const isAndroid = useMemo(() => isAndroidDevice(), []);
  const isSecure = useMemo(() => isSecureContextForPwa(), []);

  useEffect(() => {
    const mq = window.matchMedia(
      "(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)"
    );
    const sync = () => setStandalone(isStandaloneDisplayMode());
    sync();
    mq.addEventListener("change", sync);
    window.addEventListener("appinstalled", sync);
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("appinstalled", sync);
    };
  }, []);

  useEffect(() => {
    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
      setPreferAppLayoutState(true);
      writePreferAppLayout(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (standalone && readPreferAppLayout() === null) {
      setPreferAppLayoutState(true);
      writePreferAppLayout(true);
    }
  }, [standalone]);

  useEffect(() => {
    const root = document.documentElement;
    const useLayout = standalone && preferAppLayout;
    root.dataset.pwaStandalone = standalone ? "true" : "false";
    root.dataset.pwaAppLayout = useLayout ? "true" : "false";
    return () => {
      delete root.dataset.pwaStandalone;
      delete root.dataset.pwaAppLayout;
    };
  }, [standalone, preferAppLayout]);

  const setPreferAppLayout = useCallback((enabled) => {
    setPreferAppLayoutState(enabled);
    writePreferAppLayout(enabled);
  }, []);

  const dismissAndroidBanner = useCallback(() => {
    dismissForAWhile(PWA_STORAGE_KEYS.androidBannerDismissedAt);
    setAndroidDismissed(true);
  }, []);

  const dismissIosInstructions = useCallback(() => {
    dismissForAWhile(PWA_STORAGE_KEYS.iosInstructionsDismissedAt);
    setIosDismissed(true);
  }, []);

  const resetInstallHints = useCallback(() => {
    clearInstallDismissals();
    setAndroidDismissed(false);
    setIosDismissed(false);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return { outcome: choice.outcome };
  }, [deferredPrompt]);

  const isStandalone = standalone;
  const isInstalledAsApp = standalone;
  const canInstall = Boolean(deferredPrompt);
  const canInstallOnIOS = isIOS && !isStandalone;
  const showAndroidInstallBanner =
    isAndroid && !isStandalone && isSecure && canInstall && !androidDismissed;
  const showIosInstallInstructions = isIOS && !isStandalone && !iosDismissed;
  const showInstallPrompt = showAndroidInstallBanner || showIosInstallInstructions;

  return useMemo(
    () => ({
      isStandalone,
      isInstalledAsApp,
      isAndroid,
      isIOS,
      isSecure,
      canInstall,
      canInstallOnIOS,
      showAndroidInstallBanner,
      showIosInstallInstructions,
      showInstallPrompt,
      preferAppLayout,
      setPreferAppLayout,
      promptInstall,
      dismissAndroidBanner,
      dismissIosInstructions,
      resetInstallHints,
    }),
    [
      isStandalone,
      isInstalledAsApp,
      isAndroid,
      isIOS,
      isSecure,
      canInstall,
      canInstallOnIOS,
      showAndroidInstallBanner,
      showIosInstallInstructions,
      showInstallPrompt,
      preferAppLayout,
      setPreferAppLayout,
      promptInstall,
      dismissAndroidBanner,
      dismissIosInstructions,
      resetInstallHints,
    ]
  );
}

/**
 * Detects PWA / standalone mode, platform (Android vs iOS), install eligibility,
 * and returns structured install instructions for the current context.
 *
 * Must be used within `<PwaProvider>` (see `src/pwa/PwaProvider.jsx`).
 *
 * @example
 * const { isStandalone, isAndroid, isIOS, canInstall, installInstructions, promptInstall } = usePWA();
 */
export function usePWA() {
  const ctx = useContext(PwaContext);
  if (!ctx) {
    throw new Error("usePWA must be used within PwaProvider");
  }

  const installInstructions = useMemo(
    () =>
      getInstallInstructions({
        isStandalone: ctx.isStandalone,
        isAndroid: ctx.isAndroid,
        isIOS: ctx.isIOS,
        isSecure: ctx.isSecure,
        canInstall: ctx.canInstall,
        showAndroidInstallBanner: ctx.showAndroidInstallBanner,
        showIosInstallInstructions: ctx.showIosInstallInstructions,
      }),
    [
      ctx.isStandalone,
      ctx.isAndroid,
      ctx.isIOS,
      ctx.isSecure,
      ctx.canInstall,
      ctx.showAndroidInstallBanner,
      ctx.showIosInstallInstructions,
    ]
  );

  return useMemo(
    () => ({
      ...ctx,
      installInstructions,
      /** @deprecated Use isIOS */
      isIos: ctx.isIOS,
    }),
    [ctx, installInstructions]
  );
}

/** @deprecated Use usePWA */
export const usePwa = usePWA;
